import { createFileRoute } from "@tanstack/react-router";
import { Clause, LegalLayout } from "@/components/sox/LegalLayout";
import { LEGAL, LEGAL_ADDRESS_LINE } from "@/lib/legal";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — SOXIE365" },
      {
        name: "description",
        content:
          "How SOXIE365 collects, uses and protects personal data, including GDPR, UK GDPR and US state privacy rights.",
      },
      { property: "og:title", content: "Privacy Policy — SOXIE365" },
      {
        property: "og:description",
        content: "How SOXIE365 handles your personal data and the rights you have over it.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://www.soxie365.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://www.soxie365.com/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout
      index="01"
      title="Privacy Policy"
      intro={`${LEGAL.entity} ("${LEGAL.brand}", "we", "us") is the controller of the personal data described below. This policy explains what we collect, why, how long we keep it, and the rights you can exercise — under the EU GDPR, the UK GDPR, and US state privacy laws such as the CCPA/CPRA.`}
    >
      <Clause heading="1. Who we are">
        <p>
          Controller: <strong>{LEGAL.entity}</strong>, {LEGAL_ADDRESS_LINE}. Company number{" "}
          {LEGAL.registrationNumber}. VAT {LEGAL.vatNumber}.
        </p>
        <p>
          Privacy contact: <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>. We
          have not appointed a Data Protection Officer, as we are not required to.
        </p>
      </Clause>

      <Clause heading="2. What we collect">
        <ul>
          <li>
            <strong>Order data:</strong> name, email address, billing and shipping address, chosen
            variant, order reference, amount paid, and delivery status.
          </li>
          <li>
            <strong>Payment data:</strong> processed directly by Stripe. We never receive or store
            your full card number, CVC, or bank credentials — only the last four digits, card brand
            and payment outcome.
          </li>
          <li>
            <strong>Communications:</strong> messages you send us by email and our replies.
          </li>
          <li>
            <strong>Analytics data (consent-based):</strong> pages viewed, approximate location at
            country/city level, device and browser type, and referring source, collected via Google
            Analytics 4 only after you accept analytics cookies.
          </li>
          <li>
            <strong>Technical logs:</strong> IP address, timestamps and error diagnostics generated
            by our hosting provider for security and troubleshooting.
          </li>
        </ul>
        <p>
          We do not knowingly collect data from children under 16, and we do not collect special
          category data or sell personal data.
        </p>
      </Clause>

      <Clause heading="3. Why we use it and our legal basis">
        <ul>
          <li>
            <strong>To fulfil your order</strong> — payment, shipping, order confirmation and
            service emails. Legal basis: performance of a contract (GDPR Art. 6(1)(b)).
          </li>
          <li>
            <strong>To meet tax, VAT, customs and accounting obligations.</strong> Legal basis:
            legal obligation (Art. 6(1)(c)).
          </li>
          <li>
            <strong>To secure the site and prevent fraud or chargeback abuse.</strong> Legal basis:
            legitimate interests (Art. 6(1)(f)).
          </li>
          <li>
            <strong>To measure how the site is used</strong> via analytics cookies. Legal basis:
            consent (Art. 6(1)(a)), withdrawable at any time.
          </li>
        </ul>
        <p>
          We do not use your data for automated decision-making that produces legal effects, and we
          do not run profiling-based advertising.
        </p>
      </Clause>

      <Clause heading="4. Who we share it with">
        <p>We share data only with processors that help us run the store:</p>
        <ul>
          <li>
            <strong>Stripe</strong> — payment processing, fraud prevention and payment receipts.
          </li>
          <li>
            <strong>Lovable Cloud / Supabase</strong> — application hosting, database and email
            delivery.
          </li>
          <li>
            <strong>Google Analytics</strong> — website analytics, only with your consent.
          </li>
          <li>
            <strong>Shipping and customs partners</strong> — name, address and phone number needed
            to deliver and clear your parcel.
          </li>
        </ul>
        <p>
          We may also disclose data where required by law, or as part of a merger or acquisition. We
          never sell or rent your personal data, and we do not share it for cross-context behavioural
          advertising.
        </p>
      </Clause>

      <Clause heading="5. International transfers">
        <p>
          Some providers process data outside the EEA/UK, including in the United States. Those
          transfers rely on the European Commission's Standard Contractual Clauses, the UK
          International Data Transfer Addendum, and — where applicable — the provider's certification
          under the EU–US Data Privacy Framework, together with appropriate technical safeguards such
          as encryption in transit.
        </p>
      </Clause>

      <Clause heading="6. How long we keep it">
        <ul>
          <li>Order, invoice and tax records: 7 years, as required by accounting law.</li>
          <li>Support emails: 24 months from the last message.</li>
          <li>Analytics data: 14 months, then deleted or aggregated.</li>
          <li>Server and security logs: up to 12 months.</li>
        </ul>
      </Clause>

      <Clause heading="7. Your rights">
        <p>
          If you are in the EEA or UK you may request access, rectification, erasure, restriction,
          portability, and object to processing based on legitimate interests. Where processing is
          based on consent, you can withdraw it at any time without affecting prior processing.
        </p>
        <p>
          If you are a California, Colorado, Connecticut, Virginia, Texas or other US state resident,
          you may request to know, delete, or correct your personal information, opt out of any sale
          or sharing (we do neither), and are entitled to non-discrimination for exercising those
          rights. Authorised agents may submit requests on your behalf with proof of authorisation.
        </p>
        <p>
          To exercise any right, email <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>.
          We respond within 30 days (45 days for US state requests, extendable once where permitted).
          You also have the right to lodge a complaint with your local data protection authority.
        </p>
      </Clause>

      <Clause heading="8. Security">
        <p>
          Traffic is encrypted with TLS, payments are handled entirely inside Stripe's PCI-DSS
          certified environment, and access to order data is limited to the people who need it to
          fulfil your order. No system is perfectly secure, but we notify affected users and the
          relevant authority without undue delay if a breach is likely to create a risk to you.
        </p>
      </Clause>

      <Clause heading="9. Changes">
        <p>
          We update this policy when our processing changes. The version date at the top always
          reflects the current version, and material changes are announced on this page.
        </p>
      </Clause>
    </LegalLayout>
  );
}
